/**
 * 吸顶锚点滚动 Hook
 * - 滚动超过 250px 显示吸顶，否则隐藏
 * - 滚动时自动高亮当前可见区域对应的锚点
 * - 点击 tab 平滑滚动到对应区域
 */
import { useCallback, useEffect, useState } from 'react';
import { TAnchor } from '../types/common';

const STICKY_THRESHOLD = 170;
const ANCHOR_OFFSET = 100;

export interface IUseAnchorScrollOptions {
  /** 锚点顺序列表（同时也是各模块的 DOM id） */
  anchors: TAnchor[];
  defaultAnchor?: TAnchor;
  scrollRef: React.MutableRefObject<HTMLElement | null>;
}

export interface IUseAnchorScrollReturn {
  activeAnchor: TAnchor;
  isSticky: boolean;
  scrollToAnchor: (anchor: TAnchor) => void;
}

export function useAnchorScroll({
  anchors,
  defaultAnchor,
  scrollRef,
}: IUseAnchorScrollOptions): IUseAnchorScrollReturn {
  const [isSticky, setIsSticky] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState<TAnchor>(
    defaultAnchor ?? anchors[0],
  );

  const scrollToAnchor = useCallback(
    (anchor: TAnchor) => {
      const container = scrollRef.current;
      const el = document.getElementById(anchor);
      if (!container || !el) return;
      setActiveAnchor(anchor);
      container.scrollTo({ top: el.offsetTop - 5, behavior: 'smooth' });
    },
    [scrollRef],
  );

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop } = container;
      setIsSticky(scrollTop > STICKY_THRESHOLD);
      let current: TAnchor = anchors[0];
      for (const anchor of anchors) {
        const el = document.getElementById(anchor);
        if (el && scrollTop >= el.offsetTop - ANCHOR_OFFSET) {
          current = anchor;
        }
      }
      setActiveAnchor(current);
    };

    container.addEventListener('scroll', handleScroll, false);
    return () => container.removeEventListener('scroll', handleScroll, false);
  }, [anchors, scrollRef]);

  return { activeAnchor, isSticky, scrollToAnchor };
}
